import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Inject,
  Injectable,
  makeStateKey,
  OnDestroy,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import {
  Observable,
  of,
  BehaviorSubject,
  forkJoin,
  Subscription,
  EMPTY,
  throwError,
} from 'rxjs';
import {
  catchError,
  delay,
  expand,
  map,
  mergeMap,
  reduce,
  retry,
  retryWhen,
  shareReplay,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs/operators';
import {
  CaasContentResponse,
  CaasFooter,
  CaasLayout,
  CaasNewsCategoryData,
  CaasNewsData,
  CaasPageTypeGroup,
  CaasPlatformData,
  GCA,
  CaasPodcastEpisodeData,
  CaasRawLink,
  CaasReference,
  CaasRedirectsData,
} from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { environment } from '../environments/environment';
import {
  ErrorHandlerService,
  MetaData,
  PodcastItems,
  PodcastPlatforms,
  safeAssign,
} from 'hdi-components';
import { Headlines } from '../../../hdi-components/src/shared/enums/shared.enums';
import BadgeType from '../../../hdi-components/src/shared/enums/badge-type.enums';
import {
  News,
  NewsFilter,
} from '../../../hdi-components/src/shared/interfaces/news.interface';
import { GlobalServices } from './global.service';
import { CaasRoutesService } from '../../../hdi-components/src/services/caas-routes.service';
import MenuItem from '../../../hdi-components/src/shared/interfaces/menuItems.interface';
import { MetaDataService } from './meta-data.service';
import {
  ImageMetaData,
  PageGroup,
  SearchCategory,
} from '../../../hdi-components/src/shared/interfaces/meta-data.interface';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import {
  buildLogoWalls,
  mapEpisodeToPodcastItem,
} from '../helpers/caas-content.helper';
import { Redirects } from '../../../hdi-components/src/shared/interfaces/redirects.interface';

@Injectable({
  providedIn: 'root',
})
export class CaasContentService implements OnDestroy {
  private readonly apiKey = environment.cloudApiKey;
  private readonly caasUrl = environment.cloudContentUrl;

  isPreview = environment.preview ? 'preview' : 'release';

  private readonly imageUrlsSubject = new BehaviorSubject<
    { resolution: string; uid: string; url: string }[]
  >([]);
  imageUrls$ = this.imageUrlsSubject.asObservable();

  podcastItems: BehaviorSubject<PodcastItems[]> = new BehaviorSubject<
    PodcastItems[]
  >([]);
  podcastPlatforms: BehaviorSubject<PodcastPlatforms[]> = new BehaviorSubject<
    PodcastPlatforms[]
  >([]);
  news: BehaviorSubject<News[]> = new BehaviorSubject<News[]>([]);
  newsFilters: BehaviorSubject<NewsFilter[]> = new BehaviorSubject<
    NewsFilter[]
  >([]);
  searchCategories: BehaviorSubject<SearchCategory[]> = new BehaviorSubject<
    SearchCategory[]
  >([]);
  searchCategoriesSubscription$: Subscription = new Subscription();

  redirects: BehaviorSubject<Redirects[]> = new BehaviorSubject<Redirects[]>(
    []
  );

  // Define a complete GCA seed object with all required properties
  private readonly seed: GCA = {
    _id: '',
    _etag: { $oid: '' },
    streams: [],
    _returned: 0,
    _embedded: { 'rh:doc': [] },
    _links: {
      crud: {
        href: '',
      },
    },
  };

  private cache: {
    [key: string]:
      | Observable<CaasContentResponse>
      | Observable<GCA>
      | Observable<{ title: string; identifier: string }>
      | Observable<{ identifier: string; name: string }[]>
      | Observable<CaasLayout[]>
      | Observable<News[]>
      | Observable<NewsFilter[]>
      | Observable<MetaData>
      | Observable<CaasPlatformData[]>
      | Observable<CaasPodcastEpisodeData[]>
      | Observable<PageGroup[]>
      | Observable<{ layouts: CaasLayout[]; metaData: MetaData }>;
  } = {};

  private readonly CACHE_EXPIRY_TIME = 3600000; // 1 hour

  constructor(
    private readonly http: HttpClient,
    private readonly globalServices: GlobalServices,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly metaDataService: MetaDataService,
    private readonly router: Router,
    private readonly transferState: TransferState,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  /**
   * Helper function to create a complete GCA seed object
   */
  private createSeed(): GCA {
    return {
      _id: '',
      _etag: { $oid: '' },
      streams: [],
      _returned: 0,
      _embedded: { 'rh:doc': [] },
      _links: {
        crud: {
          href: '',
        },
      },
    };
  }

  getContent(identifier: string): Observable<{
    layouts: CaasLayout[];
    metaData: MetaData;
    identifier?: string;
  }> {
    const DATA_KEY_CONTENT = makeStateKey<{
      layouts: CaasLayout[];
      metaData: MetaData;
      identifier?: string;
    }>('content' + identifier);
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache[identifier]) {
      if (isPlatformBrowser(this.platformId)) {
        const serverContent = this.transferState.get(DATA_KEY_CONTENT, null);
        if (serverContent) {
          return of(serverContent);
        }
      }
      // Setze diesen Wert auf true, um den 500er-Fehler zu simulieren
      const simulateError = false;
      this.cache[identifier] = this.http
        .get<CaasContentResponse>(this.buildUrl(identifier, this.isPreview), {
          headers,
        })
        .pipe(
          retryWhen((errors) =>
            errors.pipe(
              tap(() => console.warn('Retrying request for content...')),
              delay(2000),
              take(3)
            )
          ),
          switchMap((data) => {
            let layouts: CaasLayout[] = [];
            if (data?.page?.children) {
              layouts = data.page.children.filter(
                (child): child is CaasLayout => 'children' in child
              );
            }
            // Logik zur Fehlersimulation
            if (simulateError) {
              console.log('Simulating 500 Internal Server Error...');
              return throwError(
                () =>
                  new HttpErrorResponse({
                    status: 500,
                    statusText: 'Internal Server Error getContent (Simulated)',
                  })
              );
            }
            if (data?.page?.metaFormData) {
              return this.getSearchCategories().pipe(
                map((searchCategories) => {
                  this.searchCategories.next(searchCategories);
                  const metaFormData = data.page.metaFormData;
                  let metaData: MetaData = {} as MetaData;
                  if (metaFormData && Object.keys(metaFormData).length > 0) {
                    metaData = this.metaDataService.translateMetaData(
                      metaFormData,
                      searchCategories
                    );
                  }
                  const content = {
                    layouts,
                    metaData,
                    identifier: data.page.identifier,
                  };
                  // Auf Server: Daten für Client speichern
                  if (!isPlatformBrowser(this.platformId)) {
                    this.transferState.set(DATA_KEY_CONTENT, content);
                  }

                  return content;
                })
              );
            }
            return of({
              layouts,
              metaData: {} as MetaData,
              identifier: undefined,
            });
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching meta data:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getMetaData',
              true // Redirect to 500 page on 500 errors
            );
            return of({
              layouts: [] as CaasLayout[],
              metaData: {} as MetaData,
              identifier: undefined,
            });
          })
        );
    }

    return this.cache[identifier] as Observable<{
      layouts: CaasLayout[];
      metaData: MetaData;
    }>;
  }

  getPlatforms(): Observable<CaasPlatformData[]> {
    const DATA_KEY_PLATFORMS = makeStateKey<CaasPlatformData[]>('platforms');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['platforms']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverPlatforms = this.transferState.get(
          DATA_KEY_PLATFORMS,
          null
        );
        if (serverPlatforms) {
          return of(serverPlatforms);
        }
      }
      this.cache['platforms'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{entityType:'Podcast'}",
            initialPage
          ),
          { headers }
        )
        .pipe(
          // Handle pagination by expanding the observable stream
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              const DATA_KEY_PODCAST = makeStateKey<GCA>('podcast' + nextPage);
              if (isPlatformBrowser(this.platformId)) {
                const serverPodcast = this.transferState.get(
                  DATA_KEY_PODCAST,
                  null
                );
                if (serverPodcast) {
                  return of(serverPodcast);
                }
              }
              const GCA$ = this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'Podcast'}",
                  nextPage
                ),
                { headers }
              );
              return GCA$.pipe(
                tap((gcaData) => {
                  if (!isPlatformBrowser(this.platformId)) {
                    this.transferState.set(DATA_KEY_PODCAST, gcaData);
                  }
                })
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          // Accumulate the paginated results
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Optionally, preserve or update other properties based on your logic
            };
          }, seed),
          retry(3), // Retry the entire pipeline up to 3 times upon failure
          // Transform the accumulated GCA object to the desired CaasPlatformData[] array
          mergeMap((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            const platforms = Array.isArray(rhDoc)
              ? (rhDoc as unknown as CaasPlatformData[])
              : [];
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_PLATFORMS, platforms);
            }
            return of(platforms);
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching platforms:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getPlatforms'
            );
            return of([]);
          })
        );
    }
    return this.cache['platforms'] as Observable<CaasPlatformData[]>;
  }

  getPodcastEpisodes(): Observable<CaasPodcastEpisodeData[]> {
    const DATA_KEY_PODCAST_EPISODES =
      makeStateKey<CaasPodcastEpisodeData[]>('podcastEpisodes');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['podcastEpisodes']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverEpisodes = this.transferState.get(
          DATA_KEY_PODCAST_EPISODES,
          null
        );
        if (serverEpisodes) {
          return of(serverEpisodes);
        }
      }
      this.cache['podcastEpisodes'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{entityType:'PodcastEpisode'}",
            initialPage
          ),
          { headers }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              const DATA_KEY_PODCAST_EPISODE = makeStateKey<GCA>(
                'podcastEpisode' + nextPage
              );
              if (isPlatformBrowser(this.platformId)) {
                const serverPodcastEpisode = this.transferState.get(
                  DATA_KEY_PODCAST_EPISODE,
                  null
                );
                if (serverPodcastEpisode) {
                  return of(serverPodcastEpisode);
                }
              }
              const GCA$ = this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'PodcastEpisode'}",
                  nextPage
                ),
                { headers }
              );
              return GCA$.pipe(
                tap((gcaData) => {
                  if (!isPlatformBrowser(this.platformId)) {
                    this.transferState.set(DATA_KEY_PODCAST_EPISODE, gcaData);
                  }
                })
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Preserve or update other properties as needed
            };
          }, seed),
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            const podcastEpisodes = Array.isArray(rhDoc)
              ? (rhDoc as unknown as CaasPodcastEpisodeData[])
              : [];
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(
                DATA_KEY_PODCAST_EPISODES,
                podcastEpisodes
              );
            }
            return podcastEpisodes;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching podcast episodes:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getPodcastEpisodes'
            );
            return of([]);
          })
        );
    }
    return this.cache['podcastEpisodes'] as Observable<
      CaasPodcastEpisodeData[]
    >;
  }

  getPodcastData(): Observable<{
    podcasts: PodcastItems[];
    platforms: PodcastPlatforms[];
  }> {
    return forkJoin([this.getPlatforms(), this.getPodcastEpisodes()]).pipe(
      map(([platforms, podcastEpisodes]) => {
        const logoWalls = buildLogoWalls(platforms, this.errorHandler);
        this.podcastPlatforms.next(logoWalls);
        const podcastItems = podcastEpisodes.map((episode) =>
          mapEpisodeToPodcastItem(episode, platforms, this.errorHandler)
        );

        this.podcastItems.next(podcastItems);

        return {
          podcasts: podcastItems,
          platforms: this.podcastPlatforms.getValue(),
        };
      }),
      catchError((error) => {
        console.error('Error fetching podcast data:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getPodcastData'
        );
        return of({ podcasts: [], platforms: [] });
      })
    );
  }

  getTags(): Observable<
    {
      identifier: string;
      name: string;
    }[]
  > {
    const DATA_KEY_TAGS =
      makeStateKey<{ identifier: string; name: string }[]>('tags');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['tags']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverTags = this.transferState.get(DATA_KEY_TAGS, null);
        if (serverTags) {
          return of(serverTags);
        }
      }
      this.cache['tags'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'Tag'}",
            initialPage
          ),
          { headers }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              const DATA_KEY_TAG = makeStateKey<GCA>('tag' + nextPage);
              if (isPlatformBrowser(this.platformId)) {
                const serverTag = this.transferState.get(DATA_KEY_TAG, null);
                if (serverTag) {
                  return of(serverTag);
                }
              }
              const GCA$ = this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'Tag'}",
                  nextPage
                ),
                { headers }
              );
              return GCA$.pipe(
                tap((gcaData) => {
                  if (!isPlatformBrowser(this.platformId)) {
                    this.transferState.set(DATA_KEY_TAG, gcaData);
                  }
                })
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Preserve or update other properties as needed
            };
          }, seed),
          map((data) => data._embedded?.['rh:doc'] || []),
          map((data) => {
            let identifier = '';
            let name = '';
            const tags = data.map((item) => {
              safeAssign(
                () => {
                  identifier = item?.identifier ?? '';
                },
                'CaasContentService > getTags - identifier',
                this.errorHandler
              );
              safeAssign(
                () => {
                  name = item?.['formData']?.['tt_title']?.value ?? '';
                },
                'CaasContentService > getTags - name',
                this.errorHandler
              );
              return {
                identifier,
                name,
              };
            });
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_TAGS, tags);
            }
            return tags;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching tags:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getTags'
            );
            return of([]);
          })
        );
    }
    return this.cache['tags'] as Observable<
      {
        identifier: string;
        name: string;
      }[]
    >;
  }

  getPageGroups(): Observable<PageGroup[]> {
    const DATA_KEY_PAGE_GROUPS = makeStateKey<PageGroup[]>('pageGroups');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['pageGroups']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverPageGroups = this.transferState.get(
          DATA_KEY_PAGE_GROUPS,
          null
        );
        if (serverPageGroups) {
          return of(serverPageGroups);
        }
      }
      this.cache['pageGroups'] = this.http
        .get<GCA>(
          this.buildUrl('', this.isPreview, "{'entityType':'page_group'}"),
          { headers }
        )
        .pipe(
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn('Expected an array of page groups, but got:', rhDoc);
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            const pageGroups = data.map((item) => ({
              category: item?.['displayName'] ?? 'Unknown category',
              identifier: item?.['identifier'] ?? 'Unknown identifier',
            }));
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_PAGE_GROUPS, pageGroups);
            }
            return pageGroups;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching page groups:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getPageGroups'
            );
            return of([]);
          })
        );
    }
    return this.cache['pageGroups'] as Observable<PageGroup[]>;
  }

  getTypeGroupConnectors(): Observable<SearchCategory[]> {
    const DATA_KEY_TYPE_GROUP_CONNECTORS = makeStateKey<SearchCategory[]>(
      'typeGroupConnectors'
    );
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['typeGroupConnectors']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverTypeGroupConnectors = this.transferState.get(
          DATA_KEY_TYPE_GROUP_CONNECTORS,
          null
        );
        if (serverTypeGroupConnectors) {
          return of(serverTypeGroupConnectors);
        }
      }
      this.cache['typeGroupConnectors'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'type_group_connector'}"
          ),
          { headers }
        )
        .pipe(
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn(
                'Expected an array of type group connectors, but got:',
                rhDoc
              );
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            const typeGroupConnectors = (
              data as unknown as CaasPageTypeGroup[]
            ).map((item) => ({
              identifier: item?.identifier ?? '',
              name: item?.displayName ?? '',
              pageGroupIdentifier:
                item?.formData?.tt_page_group?.value?.target?.identifier ?? '',
            }));
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(
                DATA_KEY_TYPE_GROUP_CONNECTORS,
                typeGroupConnectors
              );
            }
            return typeGroupConnectors;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching type group connectors:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getTypeGroupConnectors'
            );
            return of([]);
          })
        );
    }
    return this.cache['typeGroupConnectors'] as Observable<SearchCategory[]>;
  }

  getSearchCategories(): Observable<SearchCategory[]> {
    return forkJoin([this.getPageGroups(), this.getTypeGroupConnectors()]).pipe(
      map(([pageGroups, typeGroupConnectors]) => {
        return typeGroupConnectors.map((searchCategory) => {
          const pageGroup = pageGroups.find(
            (pg) => pg.identifier === searchCategory.pageGroupIdentifier
          );
          return {
            ...searchCategory,
            category: pageGroup?.category ?? '',
          };
        });
      }),
      tap((data) => {
        this.searchCategories.next(data);
      }),
      catchError((error) => {
        console.error('Error fetching search categories:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getSearchCategories'
        );
        return of([]);
      })
    );
  }

  getNewsCategories(): Observable<NewsFilter[]> {
    const DATA_KEY_NEWS_CATEGORIES =
      makeStateKey<NewsFilter[]>('newsCategories');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['newsCategories']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverNewsCategories = this.transferState.get(
          DATA_KEY_NEWS_CATEGORIES,
          null
        );
        if (serverNewsCategories) {
          return of(serverNewsCategories);
        }
      }
      this.cache['newsCategories'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'NewsCategory'}",
            initialPage
          ),
          { headers }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              const DATA_KEY_NEWS_CATEGORY = makeStateKey<GCA>(
                'newsCategory' + nextPage
              );
              const GCA$ = this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'NewsCategory'}",
                  nextPage
                ),
                { headers }
              );
              return GCA$.pipe(
                tap((gcaData) => {
                  if (!isPlatformBrowser(this.platformId)) {
                    this.transferState.set(DATA_KEY_NEWS_CATEGORY, gcaData);
                  }
                })
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          takeWhile((data) => data !== null),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];
            // Update _returned with the total number of accumulated items
            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
            };
          }, seed), // Initialize with a complete GCA seed
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn(
                'Expected an array of news categories, but got:',
                rhDoc
              );
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            const newsCategories = data.map((item) => {
              const newsCategoryItem = item as unknown as CaasNewsCategoryData;
              let filter = '';
              let category = '';
              let identifier = '';
              safeAssign(
                () => {
                  filter =
                    newsCategoryItem.formData.tt_newsfilter?.value?.label ?? '';
                },
                'CaasContentService > getNewsCategories - filter assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  category = newsCategoryItem.formData.tt_title?.value ?? '';
                },
                'CaasContentService > getNewsCategories - category assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  identifier =
                    newsCategoryItem.formData.tt_newsfilter?.value
                      ?.identifier ?? '';
                },
                'CaasContentService > getNewsCategories - identifier assignment',
                this.errorHandler
              );
              return {
                filter: filter,
                category: category,
                identifier: identifier,
              };
            });

            const filteredNewsCategories = newsCategories.reduce(
              (acc, { filter, category, identifier }) => {
                const existingFilter = acc.find(
                  (f: NewsFilter) => f.filter === filter
                );
                if (existingFilter) {
                  existingFilter.categories.push(category);
                } else {
                  acc.push({
                    filter: filter,
                    categories: [category],
                    identifier: identifier,
                  });
                }

                this.newsFilters.next(acc);
                return acc;
              },
              [] as NewsFilter[]
            );
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(
                DATA_KEY_NEWS_CATEGORIES,
                filteredNewsCategories
              );
            }
            return filteredNewsCategories;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching news categories:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getNewsCategories'
            );
            return of([]);
          })
        );
    }
    return this.cache['newsCategories'] as Observable<NewsFilter[]>;
  }

  getNews(): Observable<News[]> {
    const DATA_KEY_NEWS = makeStateKey<News[]>('news');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['news']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverNews = this.transferState.get(DATA_KEY_NEWS, null);
        if (serverNews) {
          return of(serverNews);
        }
      }
      this.cache['news'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'News'}",
            initialPage
          ),
          {
            headers,
          }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              const DATA_KEY_NEWS_PAGE = makeStateKey<GCA>(
                'newsPage' + nextPage
              );
              if (isPlatformBrowser(this.platformId)) {
                const serverNewsPage = this.transferState.get(
                  DATA_KEY_NEWS_PAGE,
                  null
                );
                if (serverNewsPage) {
                  return of(serverNewsPage);
                }
              }

              const GCA$ = this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'News'}",
                  nextPage
                ),
                { headers }
              );
              return GCA$.pipe(
                tap((gcaData) => {
                  if (!isPlatformBrowser(this.platformId)) {
                    this.transferState.set(DATA_KEY_NEWS_PAGE, gcaData);
                  }
                })
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Preserve or update other properties as needed
            };
          }, seed),
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn('Expected an array of news, but got:', rhDoc);
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            const newsData = data.filter(
              (item) => item['formData']?.['tt_newscategory']
            ) as unknown as CaasNewsData[];

            const news = newsData.map((newsItem) => {
              let title = '';
              let date = '';
              let description = '';
              let badges: { type: BadgeType; label: string }[] = [];
              let imageUID = '';
              let imageIdentifier = '';
              let imageSrc = '';
              let timestamp = 0;
              let rawLink: CaasRawLink | null = null;
              let identifier = '';

              safeAssign(
                () => {
                  title = newsItem.formData.tt_title?.value ?? 'Untitled';
                },
                'CaasContentService > getNews - title assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  date = this.convertDateFormat(
                    newsItem.formData.tt_date?.value ?? ''
                  );
                },
                'CaasContentService > getNews - date assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  description = newsItem.formData.tt_short?.value ?? '';
                },
                'CaasContentService > getNews - description assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  badges = newsItem?.formData?.tt_top?.value
                    ? [
                        {
                          type: BadgeType.CAPSULE,
                          label: 'TOP',
                        },
                        {
                          type: BadgeType.DEFAULT,
                          label:
                            newsItem.formData.tt_newscategory?.value?.label?.split(
                              ' ('
                            )[0] ?? '',
                        },
                      ]
                    : [
                        {
                          type: BadgeType.DEFAULT,
                          label:
                            newsItem.formData.tt_newscategory?.value?.label?.split(
                              ' ('
                            )[0] ?? '',
                        },
                      ];
                },
                'CaasContentService > getNews - badges assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  imageUID = newsItem.formData.tt_image?.value?.uid ?? '';
                },
                'CaasContentService > getNews - imageUID assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  imageIdentifier =
                    newsItem.formData.tt_image?.value?.identifier ?? '';
                },
                'CaasContentService > getNews - imageIdentifier assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  imageSrc = newsItem.formData.tt_image?.value?.url ?? '';
                },
                'CaasContentService > getNews - imageSrc assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  timestamp = new Date(
                    newsItem.formData.tt_date?.value
                  ).getTime();
                },
                'CaasContentService > getNews - timestamp assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  rawLink = newsItem.formData.tt_link;
                },
                'CaasContentService > getNews - rawLink assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  identifier = newsItem.identifier ?? '';
                },
                'CaasContentService > getNews - identifier assignment',
                this.errorHandler
              );

              return {
                title: title,
                titleClasses: '',
                titleSize: Headlines.H5,
                date: date,
                description: description,
                badges: badges,
                imageUID: imageUID,
                imageIdentifier: imageIdentifier,
                imageSrc: imageSrc,
                imageAlt: '',
                timestamp: timestamp,
                rawLink: rawLink as unknown as CaasRawLink,
                identifier: identifier,
              };
            });

            this.news.next(news);
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_NEWS, news);
            }
            return news;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching news:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getNews'
            );
            return of([]);
          })
        );
    }
    return this.cache['news'] as Observable<News[]>;
  }

  getRedirects(): Observable<Redirects[]> {
    const DATA_KEY_REDIRECTS = makeStateKey<Redirects[]>('redirects');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    const initialPage = 1;
    const seed: GCA = this.createSeed();
    if (!this.cache['redirects']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverRedirects = this.transferState.get(
          DATA_KEY_REDIRECTS,
          null
        );
        if (serverRedirects) {
          return of(serverRedirects);
        }
      }
      this.cache['redirects'] = this.http
        .get<GCA>(
          this.buildUrl(
            '',
            this.isPreview,
            "{'entityType':'Redirects'}",
            initialPage
          ),
          {
            headers,
          }
        )
        .pipe(
          expand((data, index) => {
            const nextPage = this.getNextPage(data, index + initialPage);
            if (nextPage !== null) {
              const DATA_KEY_REDIRECTS_PAGE = makeStateKey<GCA>(
                'redirectsPage' + nextPage
              );
              if (isPlatformBrowser(this.platformId)) {
                const serverRedirectsPage = this.transferState.get(
                  DATA_KEY_REDIRECTS_PAGE,
                  null
                );
                if (serverRedirectsPage) {
                  return of(serverRedirectsPage);
                }
              }

              const GCA$ = this.http.get<GCA>(
                this.buildUrl(
                  '',
                  this.isPreview,
                  "{entityType:'Redirects'}",
                  nextPage
                ),
                { headers }
              );
              return GCA$.pipe(
                tap((gcaData) => {
                  if (!isPlatformBrowser(this.platformId)) {
                    this.transferState.set(DATA_KEY_REDIRECTS_PAGE, gcaData);
                  }
                })
              );
            } else {
              return EMPTY as Observable<GCA>; // Cast EMPTY to Observable<GCA>
            }
          }),
          reduce((acc: GCA, data: GCA) => {
            const items = data._embedded?.['rh:doc'] || [];
            const accumulatedItems = [...acc._embedded['rh:doc'], ...items];

            return {
              ...acc,
              _returned: accumulatedItems.length,
              _embedded: {
                'rh:doc': accumulatedItems,
              },
              // Preserve or update other properties as needed
            };
          }, seed),
          retry(3),
          map((data) => {
            const rhDoc = data._embedded?.['rh:doc'];
            if (!Array.isArray(rhDoc)) {
              console.warn('Expected an array of redirects, but got:', rhDoc);
              return [];
            }
            return rhDoc;
          }),
          map((data) => {
            const redirectsData = data.filter(
              (item) => item['formData']
            ) as unknown as CaasRedirectsData[];

            const redirects = redirectsData.map((redirectsItem) => {
              let active = false;
              let externalTarget = '';
              let internal = false;
              let internalTarget: CaasReference | null = null;
              let note = '';
              let originalUrl = '';
              let priority = 0;
              let targetStatus = null;
              let temporary = false;

              let identifier = '';

              safeAssign(
                () => {
                  active = redirectsItem.formData.tt_active?.value ?? false;
                },
                'CaasContentService > getRedirects - active assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  externalTarget =
                    redirectsItem.formData.tt_external_target?.value ?? '';
                },
                'CaasContentService > getRedirects - externalTarget assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  internal =
                    redirectsItem.formData.tt_target_type?.value ?? false;
                },
                'CaasContentService > getRedirects - internal assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  internalTarget = redirectsItem.formData.tt_internal_target;
                },
                'CaasContentService > getRedirects - internalTarget assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  note = redirectsItem.formData.tt_note?.value ?? '';
                },
                'CaasContentService > getRedirects - note assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  originalUrl =
                    redirectsItem.formData.tt_original_url?.value ?? '';
                },
                'CaasContentService > getRedirects - originalUrl assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  priority = redirectsItem.formData.tt_priority?.value ?? 0;
                },
                'CaasContentService > getRedirects - priority assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  targetStatus =
                    redirectsItem.formData.tt_target_status?.value ?? null;
                },
                'CaasContentService > getRedirects - targetStatus assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  temporary =
                    redirectsItem.formData.tt_temporary?.value ?? false;
                },
                'CaasContentService > getRedirects - temporary assignment',
                this.errorHandler
              );
              safeAssign(
                () => {
                  identifier = redirectsItem.identifier ?? '';
                },
                'CaasContentService > getRedirects - identifier assignment',
                this.errorHandler
              );

              return {
                active: active,
                externalTarget: externalTarget,
                internal: internal,
                internalTarget: internalTarget as unknown as CaasReference,
                note: note,
                originalUrl: originalUrl,
                priority: priority,
                targetStatus: targetStatus,
                temporary: temporary,

                identifier: identifier,
              };
            });

            this.redirects.next(redirects);
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_REDIRECTS, redirects);
            }
            return redirects;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching news:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getRedirects'
            );
            return of([]);
          })
        );
    }
    return this.cache['redirects'] as Observable<Redirects[]>;
  }

  getImagesMetaData(): Observable<ImageMetaData[]> {
    const DATA_KEY_IMAGES_META = makeStateKey<ImageMetaData[]>('imagesMeta');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (isPlatformBrowser(this.platformId)) {
      const serverImagesMeta = this.transferState.get(
        DATA_KEY_IMAGES_META,
        null
      );
      if (serverImagesMeta) {
        return of(serverImagesMeta);
      }
    }
    return this.http
      .get<GCA>(
        this.buildUrl(
          '',
          this.isPreview,
          "{'fsType':'Media','mediaType':'PICTURE', '$or': [{'metaFormData.md_media_alternative.value':{'$exists':true}}, {'metaFormData.media_alternative.value':{'$exists':true}}]}"
        ),
        { headers }
      )
      .pipe(
        map((data) => {
          const rhDoc = data._embedded?.['rh:doc'];
          if (!Array.isArray(rhDoc)) {
            console.warn(
              'Expected an array of image metadata, but got:',
              rhDoc
            );
            return [];
          }
          return rhDoc;
        }),
        map((data) => {
          let identifier = '';
          let fileName = '';
          let mediaAlternative = '';
          const imagesMetaData = data.map((item) => {
            safeAssign(
              () => {
                identifier = item?.identifier ?? '';
              },
              'CaasContentService > getImagesMetaData - identifier assignment',
              this.errorHandler
            );
            safeAssign(
              () => {
                fileName = item?.fileName ?? '';
              },
              'CaasContentService > getImagesMetaData - fileName assignment',
              this.errorHandler
            );
            safeAssign(
              () => {
                mediaAlternative =
                  item.metaFormData?.md_media_alternative?.value ??
                  item.metaFormData?.media_alternative?.value ??
                  '';
              },
              'CaasContentService > getImagesMetaData - mediaAlternative assignment',
              this.errorHandler
            );
            return {
              identifier: identifier,
              fileName: fileName,
              metaFormData: {
                mediaAlternative: mediaAlternative,
              },
            };
          });
          // Auf Server: Daten für Client speichern
          if (!isPlatformBrowser(this.platformId)) {
            this.transferState.set(DATA_KEY_IMAGES_META, imagesMetaData);
          }
          return imagesMetaData;
        }),
        catchError((error) => {
          console.error('Error fetching image metadata:', error);
          this.globalServices.handleError(
            error,
            'CaasContentService > getImagesMetaData'
          );
          return of([]);
        })
      );
  }

  convertDateFormat(dateString: string): string {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return '';
    }

    // Extract the day, month, and year
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();

    // Format the date as "DD.MM.YYYY"
    return `${day}.${month}.${year}`;
  }

  getCustomScripts(): Observable<CaasLayout[]> {
    return this.getGlobalContent().pipe(
      map((content) => {
        if (!Array.isArray(content)) {
          console.warn(
            'Expected an array for global content, but got:',
            content
          );
          return [];
        }
        return content.filter((item) => item.uid === 'custom_scripts');
      }),
      catchError((error) => {
        console.error('Error fetching custom scripts:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getCustomScripts'
        );
        return of([]);
      })
    );
  }

  getGlobalContent(): Observable<CaasLayout[]> {
    const DATA_KEY_GLOBAL = makeStateKey<CaasLayout[]>('globalContent');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['global']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverGlobalContent = this.transferState.get(
          DATA_KEY_GLOBAL,
          null
        );
        if (serverGlobalContent) {
          return of(serverGlobalContent);
        }
      }
      this.cache['global'] = this.http
        .get<GCA>(this.buildUrl('', this.isPreview, "{fsType:'GCAPage'}"), {
          headers,
        })
        .pipe(
          retry(3),
          map((data) => {
            const embeddedDocs = data._embedded?.['rh:doc'];
            if (!Array.isArray(embeddedDocs)) {
              console.warn(
                'getGlobalContent: Expected _embedded["rh:doc"] to be an array, but got:',
                embeddedDocs
              );
              return [];
            }
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_GLOBAL, embeddedDocs);
            }
            return embeddedDocs;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching global content:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getGlobalContent'
            );
            return of([]);
          })
        );
    }
    return this.cache['global'] as Observable<CaasLayout[]>;
  }

  getHeader(): Observable<string[] | null> {
    return this.getGlobalContent().pipe(
      map((content) => {
        if (!Array.isArray(content)) {
          console.warn(
            'getHeader: Expected content to be an array, but got:',
            content
          );
          return null;
        }
        const headers = content.filter(
          (item) => item?.uid === 'custom_scripts' || item?.uid === 'header'
        );
        if (headers.length === 0) {
          console.warn('getHeader: No header items found.');
          return null;
        }
        // Verwende den zuletzt gefundenen Header
        const latestHeader = headers[headers.length - 1];
        const scripts: string[] = [];

        // Prüfen, ob direkt formData als String oder Objekt vorhanden ist:
        const formData = latestHeader['formData'];
        if (typeof formData === 'string' && formData.trim().length > 0) {
          scripts.push(formData);
        } else if (
          formData &&
          typeof formData === 'object' &&
          'script' in formData &&
          formData.script
        ) {
          scripts.push(formData.script);
        }

        // Falls children vorhanden sind, typisieren wir es als Array von CaasLayout
        if (
          'children' in latestHeader &&
          Array.isArray((latestHeader as { children?: CaasLayout[] }).children)
        ) {
          (latestHeader as { children: CaasLayout[] }).children.forEach(
            (child) => {
              if (
                'children' in child &&
                Array.isArray((child as { children?: CaasLayout[] }).children)
              ) {
                (child as { children: CaasLayout[] }).children.forEach(
                  (section) => {
                    if (section['formData']?.st_code?.value) {
                      scripts.push(section['formData'].st_code.value);
                    }
                  }
                );
              }
            }
          );
        }

        if (scripts.length === 0) {
          console.warn(
            'getHeader: No scripts found in expected format.',
            latestHeader['formData']
          );
          return null;
        }
        return scripts;
      }),
      catchError((error) => {
        console.error('Error fetching Header:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getHeader'
        );
        return of(null);
      })
    );
  }
  getFooter(): Observable<CaasFooter | null> {
    return this.getGlobalContent().pipe(
      map((content) => {
        if (!Array.isArray(content)) {
          console.warn(
            'getFooter: Expected content to be an array, but got:',
            content
          );
          return null;
        }

        const footers = content.filter((item) => item?.uid === 'footer');
        if (footers.length === 0) {
          console.warn('getFooter: No footer items found.');
          return null;
        }

        const latestFooter = [...footers].sort((a, b) => {
          const dateA = new Date(a?.['changeInfo']?.date).getTime() || 0;
          const dateB = new Date(b?.['changeInfo']?.date).getTime() || 0;
          return dateB - dateA;
        })[0];

        if (!latestFooter?.['formData']) {
          console.warn('getFooter: Latest footer has no formData.');
          return null;
        }

        return latestFooter['formData'];
      }),
      catchError((error) => {
        console.error('Error fetching footer:', error);
        this.globalServices.handleError(
          error,
          'CaasContentService > getFooter'
        );
        return of(null);
      })
    );
  }

  getMetaNavigation(): Observable<MenuItem[]> {
    return this.getGlobalContent().pipe(
      map((content) => {
        if (!Array.isArray(content)) {
          console.warn(
            'MetaNavigation: Expected content to be an array. Received:',
            content
          );
          return [];
        }

        const filteredContent = content.find(
          ({ uid }) => uid === 'metanavigation'
        );
        if (!filteredContent) {
          console.warn(
            'MetaNavigation: No content found with UID "metanavigation".'
          );
          return [];
        }

        const { gc_meta_navigation: metaNavigation } =
          filteredContent['formData'] || {};

        if (!metaNavigation?.value?.length) {
          console.warn('MetaNavigation: "gc_meta_navigation" has no items.');
          return [];
        }

        return metaNavigation.value
          .map((item: CaasLayout) => {
            const title = item?.['formData']?.lt_link_label?.value;
            const identifier =
              item?.['formData']?.lt_link_internal?.value?.identifier || null;
            const url = this.getLinkBasedOnType(item['formData']);

            if (!title || !url) {
              console.warn(
                'MetaNavigation: Skipping item due to missing title or URL. Item:',
                item
              );
              return null;
            }

            return { title, url, identifier };
          })
          .filter((item: MenuItem | null) => item !== null);
      }),
      catchError((error) => {
        console.error(
          'MetaNavigation: Error fetching navigation items.',
          error
        );
        this.globalServices.handleError(
          error,
          'CaasContentService > getMetaNavigation'
        );
        return of([]);
      })
    );
  }

  private getLinkBasedOnType(formData: CaasLayout): string {
    const linkType = formData['lt_link_type']?.value?.identifier;

    if (linkType === 'internal') {
      const internalLink = formData['lt_link_internal']?.value?.identifier;
      if (internalLink) {
        const seoRoute = this.caasRoutesService.getSeoRouteByIdentifier(
          internalLink,
          this.router.config
        );
        if (seoRoute) {
          return seoRoute.path ?? '404'; // Return path if found, else fallback to '404'
        } else {
          console.warn(
            'Internal link identifier does not have a matching SEO route.'
          );
          return '404';
        }
      } else {
        console.warn('Internal link identifier is missing in formData.');
        return '404';
      }
    }

    const externalLink = formData['lt_link_external']?.value;
    if (externalLink) {
      return externalLink;
    }

    console.warn('External link is missing in formData.');
    return '';
  }

  getDataset(): Observable<CaasLayout[]> {
    const DATA_KEY_DATASET = makeStateKey<CaasLayout[]>('dataset');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['dataset']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverDataset = this.transferState.get(DATA_KEY_DATASET, null);
        if (serverDataset) {
          return of(serverDataset);
        }
      }
      this.cache['dataset'] = this.http
        .get<CaasContentResponse>(
          this.buildUrl('', this.isPreview, "{fsType:'Dataset'}"),
          {
            headers,
          }
        )
        .pipe(
          retry(3),
          map((data) => {
            const embeddedDocs = data._embedded?.['rh:doc'];
            if (!Array.isArray(embeddedDocs)) {
              console.warn(
                'Expected an array of dataset items, but got:',
                embeddedDocs
              );
              return [];
            }
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_DATASET, embeddedDocs);
            }
            return embeddedDocs;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching dataset:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getDataset'
            );
            return of([]);
          })
        );
    }
    return this.cache['dataset'] as Observable<CaasLayout[]>;
  }

  getEvents(): Observable<CaasLayout[]> {
    const DATA_KEY_EVENTS = makeStateKey<CaasLayout[]>('events');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['events']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverEvents = this.transferState.get(DATA_KEY_EVENTS, null);
        if (serverEvents) {
          return of(serverEvents);
        }
      }
      this.cache['events'] = this.http
        .get<CaasContentResponse>(
          this.buildUrl('', this.isPreview, "{entityType:'Event'}"),
          {
            headers,
          }
        )
        .pipe(
          retry(3),
          map((data) => {
            const embeddedDocs = data._embedded?.['rh:doc'];
            if (!Array.isArray(embeddedDocs)) {
              console.warn(
                'Expected an array of events, but got:',
                embeddedDocs
              );
              return [];
            }
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_EVENTS, embeddedDocs);
            }
            return embeddedDocs;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching events:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getEvents'
            );
            return of([]);
          })
        );
    }
    return this.cache['events'] as Observable<CaasLayout[]>;
  }

  getMedia(): Observable<CaasLayout[]> {
    const DATA_KEY_MEDIA = makeStateKey<CaasLayout[]>('media');
    const headers = this.globalServices.createHeader(this.apiKey || '');
    if (!this.cache['media']) {
      if (isPlatformBrowser(this.platformId)) {
        const serverMedia = this.transferState.get(DATA_KEY_MEDIA, null);
        if (serverMedia) {
          return of(serverMedia);
        }
      }
      this.cache['media'] = this.http
        .get<CaasContentResponse>(
          this.buildUrl('', this.isPreview, "{fsType:'Media'}"),
          { headers }
        )
        .pipe(
          retry(3),
          map((data) => {
            const embeddedDocs = data._embedded?.['rh:doc'];
            if (!Array.isArray(embeddedDocs)) {
              console.warn(
                'Expected an array of media items, but got:',
                embeddedDocs
              );
              return [];
            }
            // Auf Server: Daten für Client speichern
            if (!isPlatformBrowser(this.platformId)) {
              this.transferState.set(DATA_KEY_MEDIA, embeddedDocs);
            }
            return embeddedDocs;
          }),
          shareReplay(1, this.CACHE_EXPIRY_TIME),
          catchError((error) => {
            console.error('Error fetching media:', error);
            this.globalServices.handleError(
              error,
              'CaasContentService > getMedia'
            );
            return of([]);
          })
        );
    }
    return this.cache['media'] as Observable<CaasLayout[]>;
  }

  private buildUrl(
    identifier: string,
    release: string = 'release',
    filter?: string,
    page?: number
  ): string {
    try {
      const urlBase = `${this.caasUrl}.${release}.content`;
      const url = filter
        ? page
          ? `${urlBase}/?filter=${encodeURIComponent(filter)}&page=${page}`
          : `${urlBase}/?filter=${encodeURIComponent(filter)}`
        : `${urlBase}/${identifier}.de_DE`;

      return url;
    } catch (error) {
      console.error('Error building URL:', error);
      return '';
    }
  }

  private getNextPage(data: GCA, currentPage: number): number | null {
    // If _returned is 0, it means there are no more items to fetch
    return data._returned > 0 ? currentPage + 1 : null;
  }

  ngOnDestroy(): void {
    try {
      if (this.searchCategoriesSubscription$) {
        this.searchCategoriesSubscription$.unsubscribe();
      }
    } catch (error) {
      console.error(
        'Error unsubscribing from searchCategoriesSubscription$',
        error
      );
    }
  }
}
